/**
 * Admin Dashboard Component
 * 
 * This component provides an admin interface for managing user permissions.
 * It includes user management, permission overview, and permission granting/revoking.
 */

import React, { useState, useEffect } from 'react';
import PermissionGate from '../auth/PermissionGate';
// Removed separate GraphAccessControl and FinancialDataPermissionManagement tabs
import { CreateUserInvitation } from './CreateUserInvitation';
import { InvitationManagement } from './InvitationManagement';
import UserPermissionsOverview from './UserPermissionsOverview';
import adminApi from '../../services/adminApi';
import './admin.css';

interface AdminDashboardProps {}

const AdminDashboard: React.FC<AdminDashboardProps> = () => {
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [overview, setOverview] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const overviewData = await adminApi.getPermissionOverview();
      setOverview(overviewData);
    } catch (err) {
      setError('Failed to load admin data');
      console.error('Error loading admin data:', err);
    } finally {
      setLoading(false);
    }
  };

  const renderOverview = () => {
    if (loading) {
      return <div className="loading">Loading overview...</div>;
    }
    
    if (!overview) {
      return <div className="error">Failed to load overview data</div>;
    }

    return (
      <div className="admin-overview">
        <h2>Permission Overview</h2>
        <div className="overview-stats">
          <div className="stat-card">
            <h3>Users</h3>
            <div className="stat-value">{overview.total_users}</div>
            <div className="stat-label">Total Users</div>
          </div>
          <div className="stat-card">
            <h3>Active Users</h3>
            <div className="stat-value">{overview.active_users}</div>
            <div className="stat-label">Currently Active</div>
          </div>
          <div className="stat-card">
            <h3>Value Chain Permissions</h3>
            <div className="stat-value">{overview.value_chain_permissions}</div>
            <div className="stat-label">Total Permissions</div>
          </div>
          <div className="stat-card">
            <h3>Country Permissions</h3>
            <div className="stat-value">{overview.country_permissions}</div>
            <div className="stat-label">Total Permissions</div>
          </div>
          <div className="stat-card">
            <h3>Data Permissions</h3>
            <div className="stat-value">{overview.data_permissions}</div>
            <div className="stat-label">Total Permissions</div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <PermissionGate 
      requiredPermissions={['admin']}
      fallback={
        <div className="admin-dashboard">
          <div className="admin-container">
            <div className="admin-card">
              <div className="auth-message error">
                <span>🚫</span>
                <div>
                  <strong>Admin Access Required</strong>
                  <p>You need admin permissions to access this dashboard.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      }
    >
      <div className="admin-dashboard">
        <div className="admin-container">
          <div className="admin-header">
            <h1>Admin Dashboard</h1>
            <p>Manage user permissions and system settings</p>
          </div>

          <div className="admin-tabs">
            <button 
              className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              Overview
            </button>
            <button 
              className={`tab-button ${activeTab === 'users-permissions' ? 'active' : ''}`}
              onClick={() => setActiveTab('users-permissions')}
            >
              Users &amp; permissions
            </button>
            {/* Graph Access Control tab removed — see Users & permissions → Bulk permission records */}
            <button 
              className={`tab-button ${activeTab === 'create-invitation' ? 'active' : ''}`}
              onClick={() => setActiveTab('create-invitation')}
            >
              Create Invitation
            </button>
            <button 
              className={`tab-button ${activeTab === 'manage-invitations' ? 'active' : ''}`}
              onClick={() => setActiveTab('manage-invitations')}
            >
              Manage Invitations
            </button>
            {/* Financial Data Permissions tab removed — see Users & permissions → Bulk permission records */}
          </div>

          <div className="admin-content">
            {error && (
              <div className="auth-message error">
                <span>⚠️</span>
                {error}
              </div>
            )}

            {activeTab === 'overview' && renderOverview()}
            {activeTab === 'users-permissions' && <UserPermissionsOverview />}
            {/* Graph Access Control content removed */}
            {activeTab === 'create-invitation' && <CreateUserInvitation onInvitationCreated={() => setActiveTab('manage-invitations')} />}
            {activeTab === 'manage-invitations' && <InvitationManagement />}
            {/* Financial Data Permissions content removed */}
          </div>
        </div>
      </div>
    </PermissionGate>
  );
};

export default AdminDashboard;
